import { Route, Footprints, Users, Backpack, Package, CloudSun, ListOrdered, BookOpen } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { formatCHF } from "@/lib/api";

const Bloc = ({ id, icon: Icon, eyebrow, titre, children }) => (
  <Reveal>
    <section id={id} className="border-t border-sand-dark pt-10" data-testid={`event-section-${id}`}>
      <p className="eyebrow mb-3 flex items-center gap-2"><Icon className="w-3.5 h-3.5 text-terra" />{eyebrow}</p>
      {titre && <h2 className="font-serif text-2xl lg:text-3xl text-bark leading-tight mb-5">{titre}</h2>}
      <div className="text-fumee leading-relaxed space-y-4">{children}</div>
    </section>
  </Reveal>
);

const Paragraphes = ({ texte }) => texte.split(/\n+/).filter(Boolean).map((p, i) => <p key={i}>{p}</p>);

const Parcours = ({ event }) => {
  const stats = [["Distance", event.distance], ["Durée", event.duree], ["Dénivelé", event.denivele]].filter(([, v]) => v);
  return (
    <div className="grid grid-cols-3 gap-4 max-w-lg">
      {stats.map(([l, v]) => (
        <div key={l} className="bg-card border border-sand-dark rounded-2xl p-4">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-fumee">{l}</p>
          <p className="font-serif text-xl text-bark mt-1">{v}</p>
        </div>
      ))}
    </div>
  );
};

const RENDU = {
  presentation: (e) => (e.presentation || e.description) && { icon: BookOpen, eyebrow: "Présentation", titre: null, body: <Paragraphes texte={e.presentation || e.description} /> },
  parcours: (e) => (e.distance || e.denivele) && { icon: Route, eyebrow: "Le parcours", titre: "Distance, durée, dénivelé", body: <Parcours event={e} /> },
  niveau: (e) => (e.niveau_marche || e.difficulte_marche) && { icon: Footprints, eyebrow: "Niveau de marche", titre: e.niveau_marche || null, body: e.difficulte_marche && <p>{e.difficulte_marche}</p> },
  public: (e, c) => e.public_prerequis && { icon: Users, eyebrow: c.publicLabel || "Pour qui", titre: null, body: <Paragraphes texte={e.public_prerequis} /> },
  equipement: (e, c) => e.equipement && { icon: Backpack, eyebrow: c.equipementLabel || "Équipement à prendre", titre: null, body: <Paragraphes texte={e.equipement} /> },
  materiel: (e, c) => (e.materiel_specifique || e.location_tambour_prix) && { icon: Package, eyebrow: c.materielLabel || "Matériel spécifique", titre: null, body: <>{e.materiel_specifique && <Paragraphes texte={e.materiel_specifique} />}{e.location_tambour_prix ? <p>Pas de tambour ? Location possible sur place : {formatCHF(e.location_tambour_prix)}, à choisir au moment de la réservation.</p> : null}</> },
  meteo: (e) => e.meteo && { icon: CloudSun, eyebrow: "Météo · maintien ou annulation", titre: null, body: <Paragraphes texte={e.meteo} /> },
  programme: (e, c) => e.programme && { icon: ListOrdered, eyebrow: c.programmeLabel || "Déroulement", titre: null, body: (
    <ol className="space-y-3">{e.programme.split(/\n+/).filter(Boolean).map((l, i) => <li key={i} className="flex gap-4"><span className="font-serif text-2xl text-terra/80 leading-none">{String(i + 1).padStart(2, "0")}</span><span className="pt-1">{l.replace(/^\d+[.)]\s*/, "")}</span></li>)}</ol>
  ) },
};

export const EventSections = ({ event, config }) => (
  <div className="space-y-12">
    {config.sections.map((id) => {
      const r = RENDU[id]?.(event, config);
      return r ? <Bloc key={id} id={id} icon={r.icon} eyebrow={r.eyebrow} titre={r.titre}>{r.body}</Bloc> : null;
    })}
    {event.faq?.length > 0 && (
      <Reveal>
        <section id="faq" className="border-t border-sand-dark pt-10" data-testid="event-section-faq">
          <p className="eyebrow mb-3">Questions fréquentes</p>
          <Accordion type="single" collapsible className="w-full">
            {event.faq.map((f, i) => (
              <AccordionItem key={i} value={`q-${i}`} data-testid={`event-faq-${i}`}>
                <AccordionTrigger className="font-serif text-lg text-bark text-left hover:no-underline">{f.question}</AccordionTrigger>
                <AccordionContent className="text-fumee leading-relaxed">{f.reponse}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
      </Reveal>
    )}
  </div>
);
